import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, setAccessToken, getAccessToken } from "./api";
import { STORAGE_KEYS } from "./constants";
import type { User } from "@shared/types";

interface AuthContextValue {
  user: User | null; isLoading: boolean; isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

interface RegisterData { email: string; phone: string; password: string; full_name: string; role: "host" | "cleaner"; }

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { setUser(null); setIsLoading(false); return; }
    try { const r = await api.get<{ user: User }>("/auth/me"); setUser(r.user); }
    catch { setAccessToken(null); setUser(null); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api.post<{ access_token: string; user: User }>("/auth/login", { email, password }, { skipAuth: true });
    setAccessToken(r.access_token); setUser(r.user);
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    const r = await api.post<{ access_token: string; user: User }>("/auth/register", data, { skipAuth: true });
    setAccessToken(r.access_token); setUser(r.user);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {} finally { setAccessToken(null); setUser(null); if (typeof localStorage !== "undefined") localStorage.removeItem(STORAGE_KEYS.USER); }
  }, []);

  return <AuthContext.Provider value={{ user, isLoading, isAuthenticated: user !== null, login, register, logout, refreshUser }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
